import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Spinner, Modal, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch, faUser, faEnvelope, faPhone, faCalendarAlt,
  faVenusMars, faMapMarkerAlt, faCheckCircle, faExclamationTriangle,
  faPlus, faEdit, faTrash, faSyncAlt, faKey,
  faBolt, faCircle, faUserShield, faIdCard,
  faTimes, faEye, faEyeSlash, faAddressCard,
  faUserPlus, faEraser
} from '@fortawesome/free-solid-svg-icons';
import Dexie from 'dexie';
import API_CONFIG2 from '../../API_CONFIG/API_CONFIG2';

// ============================================================
// 1. UTILITAIRE FETCH AVEC RETRY (gestion 429)
// ============================================================
const fetchWithRetry = async (url, options = {}, retries = 3, baseDelay = 1000) => {
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const response = await fetch(url, options);
      if (response.status === 429 && attempt < retries) {
        const retryAfter = response.headers.get('Retry-After');
        const delay = retryAfter
          ? parseInt(retryAfter, 10) * 1000
          : baseDelay * Math.pow(2, attempt);
        console.warn(`⏳ 429 détecté, nouvelle tentative dans ${delay}ms`);
        await new Promise(resolve => setTimeout(resolve, delay));
        continue;
      }
      const text = await response.text();
      if (!response.ok) throw new Error(`HTTP ${response.status} : ${text || 'Erreur inconnue'}`);
      try {
        return JSON.parse(text);
      } catch (parseError) {
        throw new Error(`Réponse JSON invalide : ${text.substring(0, 200) || '(réponse vide)'}`);
      }
    } catch (error) {
      if (attempt === retries) throw error;
      const delay = baseDelay * Math.pow(2, attempt);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
};

// ============================================================
// 2. CACHE DEXIE (24h)
// ============================================================
const db = new Dexie('AdminsCacheDB');
db.version(1).stores({ admins: 'key, data, timestamp' });
const CACHE_DURATION = 24 * 60 * 60 * 1000;

async function getCachedAdmins(key, allowExpired = false) {
  try {
    const entry = await db.admins.get(key);
    if (!entry) return null;
    const age = Date.now() - entry.timestamp;
    if (!allowExpired && age > CACHE_DURATION) return null;
    return entry.data;
  } catch { return null; }
}

async function setCachedAdmins(key, data) {
  await db.admins.put({ key, data, timestamp: Date.now() });
}

async function getStaleAdmins(key) {
  return getCachedAdmins(key, true);
}

const API_URL = `${API_CONFIG2.apiUrl}admin/api_admin.php`;

// ============================================================
// 3. COMPOSANT PRINCIPAL
// ============================================================
const ManageAdmins = () => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, totalRecords: 0, limit: 10 });
  const [dataSource, setDataSource] = useState('cache');
  const [toast, setToast] = useState(null);

  // Refs
  const searchTimeoutRef = useRef(null);
  const abortControllerRef = useRef(null);
  const toastTimeoutRef = useRef(null);
  const isFetchingRef = useRef(false);

  // Modal
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    nom: '', prenom: '', email: '', fonction: '', adresse: '',
    tel: '', date_naisse: '', sexe: '', status: 1, pw: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);

  // ============================================================
  // 4. GESTION DES TOASTS
  // ============================================================
  const showToast = useCallback((msg, variant = 'success') => {
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    setToast({ msg, variant });
    toastTimeoutRef.current = setTimeout(() => {
      setToast(null);
      toastTimeoutRef.current = null;
    }, 5000);
  }, []);

  // ============================================================
  // 5. RÉCUPÉRATION DES ADMINS (avec cache, 429, sémaphore)
  // ============================================================
  const fetchAdmins = useCallback(async (page = 1, search = '', forceRefresh = false) => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    if (!forceRefresh && isFetchingRef.current) return;
    isFetchingRef.current = true;

    if (forceRefresh) setRefreshing(true);
    else setLoading(true);
    setDataSource('');

    const cacheKey = `page_${page}_search_${search}`;

    try {
      if (!forceRefresh) {
        const cached = await getCachedAdmins(cacheKey);
        if (cached) {
          setAdmins(cached.data);
          setPagination(cached.pagination);
          setDataSource('cache');
          setLoading(false);
          isFetchingRef.current = false;
          return;
        }
      }

      const uniqueParam = new Date().getTime();
      const url = `${API_URL}?page=${page}&limit=${pagination.limit}&search=${encodeURIComponent(search)}&unique=${uniqueParam}`;
      const data = await fetchWithRetry(url, { signal: controller.signal });

      if (data.status === 'success') {
        const payload = { data: data.data, pagination: data.pagination };
        setAdmins(data.data);
        setPagination(data.pagination);
        setDataSource(forceRefresh ? 'live (forcé)' : 'live');
        await setCachedAdmins(cacheKey, payload);
        if (forceRefresh) showToast('Données actualisées', 'success');
      } else {
        throw new Error(data.message || 'Erreur serveur');
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('fetchAdmins error:', err);
      const stale = await getStaleAdmins(cacheKey);
      if (stale) {
        setAdmins(stale.data);
        setPagination(stale.pagination);
        setDataSource('cache (périmé)');
        showToast('Erreur – Utilisation du cache', 'warning');
      } else {
        showToast(err.message || 'Erreur de chargement', 'danger');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
      isFetchingRef.current = false;
    }
  }, [pagination.limit, showToast]);

  // ============================================================
  // 6. RECHERCHE (debounce)
  // ============================================================
  const handleSearch = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      fetchAdmins(1, val, false);
    }, 400);
  };

  const handleRefresh = () => {
    if (!refreshing && !loading) {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      fetchAdmins(pagination.currentPage, searchTerm, true);
    }
  };

  const handleRefreshForce = () => {
    if (!refreshing && !loading) {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      db.admins.clear();
      fetchAdmins(pagination.currentPage, searchTerm, true);
    }
  };

  const handleClearCache = useCallback(async () => {
    if (refreshing || loading) return;
    try {
      await db.admins.clear();
      showToast('Cache vidé avec succès', 'success');
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      fetchAdmins(pagination.currentPage, searchTerm, true);
    } catch (err) {
      showToast('Erreur lors du vidage du cache', 'danger');
    }
  }, [fetchAdmins, pagination.currentPage, searchTerm, refreshing, loading, showToast]);

  // ============================================================
  // 7. INITIALISATION
  // ============================================================
  useEffect(() => {
    fetchAdmins(1, '', false);
    return () => {
      if (abortControllerRef.current) abortControllerRef.current.abort();
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ============================================================
  // 8. FORMULAIRE
  // ============================================================
  const resetForm = () => {
    setForm({
      nom: '', prenom: '', email: '', fonction: '', adresse: '',
      tel: '', date_naisse: '', sexe: '', status: 1, pw: ''
    });
    setFormErrors({});
    setEditingId(null);
    setShowPassword(false);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value
    }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!form.nom.trim()) errors.nom = 'Le nom est requis';
    if (!form.prenom.trim()) errors.prenom = 'Le prénom est requis';
    if (!form.email.trim()) errors.email = "L'email est requis";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errors.email = 'Email invalide';
    if (!form.fonction.trim()) errors.fonction = 'La fonction est requise';
    if (!form.adresse.trim()) errors.adresse = "L'adresse est requise";
    if (!editingId && !form.pw) errors.pw = 'Le mot de passe est requis';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const openAddModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (admin) => {
    setForm({
      nom: admin.nom || '',
      prenom: admin.prenom || '',
      email: admin.email || '',
      fonction: admin.fonction || '',
      adresse: admin.adresse || '',
      tel: admin.tel || '',
      date_naisse: admin.date_naisse || '',
      sexe: admin.sexe || '',
      status: admin.status ?? 1,
      pw: ''
    });
    setEditingId(admin.id);
    setFormErrors({});
    setShowModal(true);
  };

  // ============================================================
  // 9. CRUD HANDLERS (avec fetchWithRetry)
  // ============================================================
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitting(true);
    const payload = { ...form };
    if (editingId && !payload.pw) delete payload.pw;

    try {
      let data;
      if (editingId) {
        data = await fetchWithRetry(`${API_URL}?id=${editingId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      } else {
        data = await fetchWithRetry(API_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      }
      if (data.status === 'success') {
        showToast(editingId ? 'Utilisateur modifié' : 'Utilisateur créé', 'success');
        setShowModal(false);
        resetForm();
        // Vider le cache et recharger
        await db.admins.clear();
        fetchAdmins(pagination.currentPage, searchTerm, true);
      } else {
        throw new Error(data.message || 'Erreur inconnue');
      }
    } catch (err) {
      showToast(err.message, 'danger');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('⚠️ Supprimer définitivement cet utilisateur ?')) return;
    try {
      const data = await fetchWithRetry(`${API_URL}?id=${id}`, { method: 'DELETE' });
      if (data.status === 'success') {
        showToast('Utilisateur supprimé', 'success');
        await db.admins.clear();
        fetchAdmins(pagination.currentPage, searchTerm, true);
      } else {
        throw new Error(data.message || 'Erreur');
      }
    } catch (err) {
      showToast(err.message, 'danger');
    }
  };

  // ============================================================
  // 10. AFFICHAGE DU CHARGEMENT
  // ============================================================
  if (loading && !admins.length && !refreshing) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="text-secondary mt-2">Chargement des utilisateurs...</p>
      </div>
    );
  }

  // ============================================================
  // 11. RENDU PRINCIPAL
  // ============================================================
  return (
    <>
      <style>{`
        /* ============================================================
           DARK BLUE GLASSMORPHISM – MANAGE ADMINS
           ============================================================ */
        :root {
          --accent-blue: #3b82f6;
          --accent-dark-blue: #1d4ed8;
          --accent-blue-dim: rgba(59, 130, 246, 0.10);
          --accent-blue-glow: rgba(59, 130, 246, 0.25);
          --bg-primary: #0b1428;
          --bg-gradient: radial-gradient(circle at 20% 30%, #132044, #0a0f1f);
          --card-bg: rgba(20, 40, 80, 0.55);
          --card-border: rgba(59, 130, 246, 0.15);
          --text-primary: #e0e7ff;
          --text-secondary: #94a3b8;
          --text-muted: #64748b;
          --radius: 16px;
          --shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
        }

        .admin-list {
          background: var(--bg-gradient);
          min-height: 100vh;
          padding: 30px;
          color: var(--text-primary);
          font-family: 'Inter', sans-serif;
        }

        .glass-card {
          background: var(--card-bg);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          border: 1px solid var(--card-border);
          border-radius: var(--radius);
          padding: 1.8rem;
          box-shadow: var(--shadow);
          transition: all 0.2s ease;
        }
        .glass-card:hover {
          border-color: rgba(59, 130, 246, 0.25);
          box-shadow: 0 12px 40px rgba(59, 130, 246, 0.05);
        }

        .badge-source {
          font-size: 11px;
          padding: 4px 12px;
          border-radius: 40px;
          background: rgba(59,130,246,0.15);
          color: var(--accent-blue);
          border: 1px solid rgba(59,130,246,0.12);
          white-space: nowrap;
        }
        .badge-source.live {
          background: rgba(46,204,113,0.15);
          color: #2ecc71;
          border-color: rgba(46,204,113,0.12);
        }
        .badge-source.warning {
          background: rgba(245,158,11,0.15);
          color: #f59e0b;
          border-color: rgba(245,158,11,0.12);
        }

        .monetra-btn {
          padding: 10px 20px;
          border-radius: 30px;
          border: 1px solid rgba(255,255,255,0.06);
          background: rgba(255,255,255,0.04);
          color: var(--text-primary);
          transition: all 0.25s ease;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-weight: 500;
          cursor: pointer;
          font-size: 0.9rem;
          white-space: nowrap;
        }
        .monetra-btn:hover:not(:disabled) {
          background: rgba(255,255,255,0.08);
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(0,0,0,0.2);
        }
        .monetra-btn:disabled {
          opacity: 0.4;
          pointer-events: none;
        }
        .monetra-btn.primary {
          background: var(--accent-blue);
          color: #fff;
          border: none;
          box-shadow: 0 6px 20px var(--accent-blue-glow);
        }
        .monetra-btn.primary:hover:not(:disabled) {
          background: var(--accent-dark-blue);
          box-shadow: 0 8px 28px rgba(59, 130, 246, 0.4);
        }
        .monetra-btn.danger {
          background: rgba(239, 68, 68, 0.12);
          color: #f87171;
          border: 1px solid rgba(239, 68, 68, 0.2);
        }
        .monetra-btn.danger:hover:not(:disabled) {
          background: rgba(239, 68, 68, 0.25);
        }
        .monetra-btn.icon-only {
          padding: 10px;
          border-radius: 30px;
        }
        .monetra-btn.outline {
          border-color: rgba(255,255,255,0.08);
          color: var(--text-secondary);
        }
        .monetra-btn.outline:hover {
          background: rgba(255,255,255,0.05);
        }

        .search-wrapper {
          position: relative;
          display: flex;
          align-items: center;
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 30px;
          padding: 4px 4px 4px 20px;
          transition: all 0.3s ease;
          flex: 1;
          max-width: 400px;
        }
        .search-wrapper:focus-within {
          border-color: var(--accent-blue);
          box-shadow: 0 0 20px rgba(59, 130, 246, 0.08);
        }
        .search-wrapper input {
          background: transparent;
          border: none;
          color: var(--text-primary);
          outline: none;
          padding: 10px 0;
          flex: 1;
          font-size: 0.95rem;
        }
        .search-wrapper input::placeholder {
          color: var(--text-muted);
        }

        /* Tableau desktop (caché en mobile) */
        .desktop-table {
          display: block;
        }
        .glass-table {
          width: 100%;
          border-collapse: collapse;
        }
        .glass-table th {
          padding: 16px 12px;
          text-align: left;
          font-size: 0.7rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: var(--accent-blue);
          border-bottom: 1px solid rgba(255,255,255,0.04);
          font-weight: 600;
        }
        .glass-table td {
          padding: 14px 12px;
          border-bottom: 1px solid rgba(255,255,255,0.02);
          vertical-align: middle;
        }
        .glass-table tbody tr {
          transition: background 0.15s;
        }
        .glass-table tbody tr:hover {
          background: rgba(255,255,255,0.03);
        }

        .badge-status {
          display: inline-block;
          padding: 4px 12px;
          border-radius: 20px;
          font-size: 0.75rem;
          font-weight: 500;
        }
        .badge-status.active {
          background: rgba(59, 130, 246, 0.15);
          color: var(--accent-blue);
        }
        .badge-status.inactive {
          background: rgba(239, 68, 68, 0.15);
          color: #f87171;
        }

        .badge-function {
          background: var(--accent-blue-dim);
          color: var(--accent-blue);
          padding: 4px 14px;
          border-radius: 30px;
          font-size: 0.75rem;
          font-weight: 500;
          border: 1px solid rgba(59, 130, 246, 0.08);
        }

        .avatar-circle {
          width: 34px;
          height: 34px;
          border-radius: 50%;
          background: linear-gradient(135deg, var(--accent-blue), var(--accent-dark-blue));
          color: #fff;
          font-weight: 700;
          font-size: 0.85rem;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          box-shadow: 0 0 12px rgba(59, 130, 246, 0.15);
        }

        /* Cartes mobiles (cachées par défaut) */
        .mobile-cards {
          display: none;
          flex-direction: column;
          gap: 12px;
          padding: 1rem 0;
        }
        .admin-card-mobile {
          background: rgba(255,255,255,0.02);
          border: 1px solid rgba(255,255,255,0.04);
          border-radius: 16px;
          padding: 1rem;
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
        .admin-card-mobile .card-header {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .admin-card-mobile .avatar-circle {
          width: 40px;
          height: 40px;
          font-size: 1rem;
        }
        .admin-card-mobile .admin-name {
          font-weight: 600;
          font-size: 1rem;
        }
        .admin-card-mobile .admin-email {
          font-size: 0.8rem;
          color: var(--text-secondary);
        }
        .admin-card-mobile .card-meta {
          display: flex;
          flex-wrap: wrap;
          gap: 6px 12px;
          font-size: 0.8rem;
          color: var(--text-secondary);
        }
        .admin-card-mobile .card-meta .label {
          color: var(--text-muted);
          text-transform: uppercase;
          font-size: 0.7rem;
          font-weight: 500;
        }
        .admin-card-mobile .card-actions {
          display: flex;
          justify-content: flex-end;
          gap: 8px;
        }
        .admin-card-mobile .card-actions .monetra-btn {
          padding: 8px 14px;
          font-size: 0.85rem;
        }

        .toast-container {
          position: fixed;
          top: 24px;
          right: 24px;
          z-index: 9999;
          display: flex;
          flex-direction: column;
          gap: 12px;
          max-width: 440px;
        }
        .toast-item {
          background: rgba(10, 20, 45, 0.8);
          backdrop-filter: blur(24px);
          -webkit-backdrop-filter: blur(24px);
          border: 1px solid rgba(59, 130, 246, 0.2);
          border-radius: var(--radius);
          padding: 14px 20px;
          color: var(--text-primary);
          display: flex;
          align-items: center;
          gap: 14px;
          box-shadow: 0 8px 32px rgba(0,0,0,0.4);
          animation: slideIn 0.35s ease;
        }
        .toast-item.success { border-left: 4px solid var(--accent-blue); }
        .toast-item.warning { border-left: 4px solid #f59e0b; }
        .toast-item.danger { border-left: 4px solid #ef4444; }
        @keyframes slideIn {
          from { opacity: 0; transform: translateX(40px) scale(0.96); }
          to { opacity: 1; transform: translateX(0) scale(1); }
        }
        .btn-close-custom {
          background: transparent;
          border: none;
          color: var(--text-muted);
          cursor: pointer;
          font-size: 1.2rem;
          padding: 0 4px;
          margin-left: auto;
          transition: color 0.2s;
        }
        .btn-close-custom:hover { color: var(--text-primary); }

        .modal-glass .modal-content {
          background: rgba(10, 20, 45, 0.92);
          backdrop-filter: blur(30px) saturate(1.2);
          -webkit-backdrop-filter: blur(30px) saturate(1.2);
          border: 1px solid rgba(59, 130, 246, 0.12);
          border-radius: 28px;
          box-shadow: 0 40px 90px rgba(0,0,0,0.8), 0 0 0 1px rgba(59, 130, 246, 0.04) inset;
          overflow: hidden;
        }
        .modal-glass .modal-header {
          border-bottom: 1px solid rgba(59, 130, 246, 0.06);
          padding: 1.8rem 2.2rem;
          background: rgba(0, 0, 0, 0.15);
        }
        .modal-title-wrapper {
          display: flex;
          align-items: center;
          gap: 14px;
        }
        .modal-title-icon {
          width: 44px;
          height: 44px;
          border-radius: 14px;
          background: var(--accent-blue-dim);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.3rem;
          color: var(--accent-blue);
          box-shadow: 0 0 20px rgba(59, 130, 246, 0.08);
        }
        .modal-title-text {
          font-size: 1.4rem;
          font-weight: 600;
          letter-spacing: -0.3px;
          background: linear-gradient(135deg, #e0e7ff 60%, #94a3b8);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .modal-title-id {
          font-size: 0.85rem;
          font-weight: 400;
          color: var(--text-muted);
          margin-left: 8px;
          background: rgba(255,255,255,0.04);
          padding: 2px 12px;
          border-radius: 30px;
          border: 1px solid rgba(255,255,255,0.04);
        }
        .modal-glass .modal-body { padding: 2rem 2.2rem; }
        .modal-glass .modal-footer {
          border-top: 1px solid rgba(59, 130, 246, 0.06);
          padding: 1.2rem 2.2rem;
          background: rgba(0, 0, 0, 0.1);
        }

        .form-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1.2rem 2rem;
        }
        .form-group label {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 6px;
          font-size: 0.8rem;
          font-weight: 500;
          color: var(--text-secondary);
          letter-spacing: 0.3px;
          text-transform: uppercase;
        }
        .form-group label .form-icon {
          color: var(--accent-blue);
          font-size: 0.8rem;
          opacity: 0.6;
        }
        .form-group label .required {
          color: #f87171;
          margin-left: 2px;
        }

        .input-glass {
          display: flex;
          align-items: center;
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(255, 255, 255, 0.06);
          border-radius: 14px;
          transition: all 0.25s ease;
          position: relative;
        }
        .input-glass:focus-within {
          border-color: var(--accent-blue);
          background: rgba(255, 255, 255, 0.05);
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.08), 0 4px 20px rgba(59, 130, 246, 0.05);
        }
        .input-glass input,
        .input-glass select {
          flex: 1;
          background: transparent;
          border: none;
          padding: 12px 16px;
          color: var(--text-primary);
          outline: none;
          font-family: inherit;
          font-size: 0.95rem;
        }
        .input-glass select {
          appearance: none;
          background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
          background-repeat: no-repeat;
          background-position: right 12px center;
          background-size: 16px;
        }
        .input-glass select option { background: #0b1428; color: var(--text-primary); }
        .input-glass .input-append {
          padding: 0 14px;
          color: var(--text-muted);
          cursor: pointer;
          transition: color 0.2s;
        }
        .input-glass .input-append:hover { color: var(--text-primary); }
        .input-glass.error {
          border-color: #f87171;
          background: rgba(239, 68, 68, 0.03);
          box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.08);
        }
        .error-text {
          color: #f87171;
          font-size: 0.75rem;
          margin-top: 4px;
          padding-left: 12px;
        }

        .btn-modal-cancel,
        .btn-modal-submit {
          padding: 10px 28px;
          border-radius: 30px;
          font-weight: 500;
          transition: all 0.25s ease;
          border: none;
        }
        .btn-modal-cancel {
          background: rgba(255, 255, 255, 0.04);
          color: var(--text-secondary);
          border: 1px solid rgba(255, 255, 255, 0.06);
        }
        .btn-modal-cancel:hover {
          background: rgba(255, 255, 255, 0.08);
          color: var(--text-primary);
          transform: translateY(-2px);
        }
        .btn-modal-submit {
          background: var(--accent-blue);
          color: #fff;
          box-shadow: 0 6px 24px rgba(59, 130, 246, 0.2);
        }
        .btn-modal-submit:hover {
          background: var(--accent-dark-blue);
          transform: translateY(-2px);
          box-shadow: 0 8px 32px rgba(59, 130, 246, 0.3);
        }

        .flex-between {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 12px;
        }
        .gap-2 { gap: 8px; }
        .mb-3 { margin-bottom: 1.5rem; }
        .text-secondary { color: var(--text-secondary); }
        .text-muted { color: var(--text-muted); }
        .fw-bold { font-weight: 600; }
        .small { font-size: 0.85rem; }
        .py-5 { padding-top: 3rem; padding-bottom: 3rem; }
        .text-center { text-align: center; }
        .mt-2 { margin-top: 0.5rem; }
        .me-1 { margin-right: 0.25rem; }
        .me-2 { margin-right: 0.5rem; }
        .p-3 { padding: 1rem; }
        .p-0 { padding: 0; }
        .overflow-hidden { overflow: hidden; }
        .table-responsive { overflow-x: auto; }
        .border-top { border-top: 1px solid rgba(255,255,255,0.04); }
        .border-opacity-10 { opacity: 0.1; }

        /* ============ RESPONSIVE MOBILE ============ */
        @media (max-width: 768px) {
          .admin-list { padding: 16px; }
          .desktop-table { display: none; }
          .mobile-cards { display: flex; }
          .modal-glass .modal-header { padding: 1.2rem 1.5rem; }
          .modal-glass .modal-body { padding: 1.5rem; }
          .modal-glass .modal-footer { padding: 1rem 1.5rem; }
          .form-grid { grid-template-columns: 1fr; gap: 1rem; }
          .modal-title-text { font-size: 1.1rem; }
          .modal-title-icon { width: 36px; height: 36px; font-size: 1rem; }
          .monetra-btn { font-size: 0.85rem; }
          .search-wrapper { max-width: 100%; }
        }
        @media (max-width: 576px) {
          .admin-list { padding: 10px; }
          .glass-card { padding: 1rem; }
          .monetra-btn { padding: 8px 14px; font-size: 0.8rem; }
          .admin-card-mobile .admin-name { font-size: 0.9rem; }
          .admin-card-mobile .admin-email { font-size: 0.75rem; }
        }
      `}</style>

      <div className="admin-list">
        {/* En-tête et boutons */}
        <div className="flex-between mb-3">
          <div>
            <h1 className="display-6 fw-bold mb-1">👤 Utilisateurs</h1>
            <p className="text-secondary m-0">Gestion des administrateurs</p>
          </div>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <span className={`badge-source ${dataSource === 'live' || dataSource === 'live (forcé)' ? 'live' : dataSource === 'cache (périmé)' ? 'warning' : ''}`}>
              {dataSource === 'cache' ? '📦 Cache' :
               dataSource === 'cache (périmé)' ? '⚠️ Cache périmé' :
               dataSource === 'live' ? '🔄 Live' :
               dataSource === 'live (forcé)' ? '🔄 Forcé' : '📦 Cache'}
            </span>
            <button className="monetra-btn primary" onClick={openAddModal}>
              <FontAwesomeIcon icon={faPlus} /> Ajouter
            </button>
            <button className="monetra-btn outline" onClick={handleRefresh} disabled={refreshing || loading}>
              <FontAwesomeIcon icon={faSyncAlt} spin={refreshing} /> Actualiser
            </button>
            <button className="monetra-btn outline" onClick={handleRefreshForce} disabled={refreshing || loading}>
              <FontAwesomeIcon icon={faBolt} spin={refreshing} style={{ color: '#ef4444' }} /> Forcer
            </button>
            <button className="monetra-btn danger" onClick={handleClearCache} disabled={refreshing || loading}>
              <FontAwesomeIcon icon={faEraser} /> Vider le cache
            </button>
          </div>
        </div>

        {/* Search + stats */}
        <div className="glass-card mb-3">
          <div className="flex-between">
            <div className="search-wrapper">
              <FontAwesomeIcon icon={faSearch} className="text-muted" />
              <input
                type="text"
                placeholder="Rechercher (nom, prénom, email, téléphone)"
                value={searchTerm}
                onChange={handleSearch}
              />
            </div>
            <div className="text-secondary small">
              <span className="fw-bold">{pagination?.totalRecords || 0}</span> utilisateurs
            </div>
          </div>
        </div>

        {/* Tableau desktop */}
        <div className="glass-card p-0 overflow-hidden desktop-table">
          <div className="table-responsive">
            <table className="glass-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Nom complet</th>
                  <th>Email</th>
                  <th>Téléphone</th>
                  <th>Fonction</th>
                  <th>Statut</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && !admins.length && !refreshing ? (
                  <tr><td colSpan="7" className="text-center py-5"><div className="spinner-border text-primary" role="status"></div></td></tr>
                ) : admins.length === 0 ? (
                  <tr><td colSpan="7" className="text-center py-5 text-secondary">Aucun utilisateur trouvé.</td></tr>
                ) : (
                  admins.map(item => (
                    <tr key={item.id}>
                      <td><span className="fw-bold text-secondary">#{item.id}</span></td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <div className="avatar-circle">
                            {item.prenom?.charAt(0)}{item.nom?.charAt(0)}
                          </div>
                          <span>{item.prenom} {item.nom}</span>
                        </div>
                      </td>
                      <td><FontAwesomeIcon icon={faEnvelope} className="me-2 text-muted" />{item.email}</td>
                      <td><FontAwesomeIcon icon={faPhone} className="me-2 text-muted" />{item.tel || '—'}</td>
                      <td><span className="badge-function">{item.fonction}</span></td>
                      <td>
                        <span className={`badge-status ${item.status == 1 ? 'active' : 'inactive'}`}>
                          <FontAwesomeIcon icon={faCircle} className="me-1" />
                          {item.status == 1 ? 'Actif' : 'Inactif'}
                        </span>
                      </td>
                      <td>
                        <div className="d-flex gap-2">
                          <button className="monetra-btn icon-only" onClick={() => openEditModal(item)} title="Modifier">
                            <FontAwesomeIcon icon={faEdit} />
                          </button>
                          <button className="monetra-btn icon-only danger" onClick={() => handleDelete(item.id)} title="Supprimer">
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="p-3 border-top border-secondary border-opacity-10 flex-between">
            <span className="text-secondary small">Page {pagination?.currentPage || 1} sur {pagination?.totalPages || 1}</span>
            <div className="d-flex gap-2">
              <button
                className="monetra-btn icon-only"
                disabled={!pagination || pagination.currentPage <= 1 || loading || refreshing}
                onClick={() => fetchAdmins(pagination.currentPage - 1, searchTerm)}
              >
                ‹
              </button>
              <button
                className="monetra-btn icon-only"
                disabled={!pagination || pagination.currentPage >= pagination.totalPages || loading || refreshing}
                onClick={() => fetchAdmins(pagination.currentPage + 1, searchTerm)}
              >
                ›
              </button>
            </div>
          </div>
        </div>

        {/* Cartes mobiles */}
        <div className="mobile-cards">
          {loading && !admins.length && !refreshing ? (
            <div className="text-center py-5 text-secondary">Chargement...</div>
          ) : admins.length === 0 ? (
            <div className="text-center py-5 text-secondary">Aucun utilisateur trouvé.</div>
          ) : (
            admins.map(item => (
              <div key={item.id} className="admin-card-mobile">
                <div className="card-header">
                  <div className="avatar-circle">
                    {item.prenom?.charAt(0)}{item.nom?.charAt(0)}
                  </div>
                  <div>
                    <div className="admin-name">{item.prenom} {item.nom}</div>
                    <div className="admin-email">
                      <FontAwesomeIcon icon={faEnvelope} className="me-1" />{item.email}
                    </div>
                  </div>
                </div>
                <div className="card-meta">
                  <span><span className="label">Fonction</span> {item.fonction}</span>
                  <span><span className="label">Tél</span> {item.tel || '—'}</span>
                  <span><span className="label">Statut</span>
                    <span className={`badge-status ${item.status == 1 ? 'active' : 'inactive'}`}>
                      <FontAwesomeIcon icon={faCircle} className="me-1" />
                      {item.status == 1 ? 'Actif' : 'Inactif'}
                    </span>
                  </span>
                </div>
                <div className="card-actions">
                  <button className="monetra-btn outline" onClick={() => openEditModal(item)}>
                    <FontAwesomeIcon icon={faEdit} className="me-1" /> Modifier
                  </button>
                  <button className="monetra-btn danger" onClick={() => handleDelete(item.id)}>
                    <FontAwesomeIcon icon={faTrash} className="me-1" /> Supprimer
                  </button>
                </div>
              </div>
            ))
          )}
          {/* Pagination mobile */}
          <div className="p-3 border-top border-secondary border-opacity-10 flex-between">
            <span className="text-secondary small">Page {pagination?.currentPage || 1} sur {pagination?.totalPages || 1}</span>
            <div className="d-flex gap-2">
              <button
                className="monetra-btn icon-only"
                disabled={!pagination || pagination.currentPage <= 1 || loading || refreshing}
                onClick={() => fetchAdmins(pagination.currentPage - 1, searchTerm)}
              >
                ‹
              </button>
              <button
                className="monetra-btn icon-only"
                disabled={!pagination || pagination.currentPage >= pagination.totalPages || loading || refreshing}
                onClick={() => fetchAdmins(pagination.currentPage + 1, searchTerm)}
              >
                ›
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modal */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        size="lg"
        centered
        backdrop="static"
        className="modal-glass"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <div className="modal-title-wrapper">
              <div className="modal-title-icon">
                <FontAwesomeIcon icon={editingId ? faEdit : faUserPlus} />
              </div>
              <div>
                <span className="modal-title-text">
                  {editingId ? 'Modifier' : 'Ajouter'} un utilisateur
                </span>
                {editingId && <span className="modal-title-id"># {editingId}</span>}
              </div>
            </div>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <div className="form-grid">
              <div className="form-group">
                <Form.Label>
                  <FontAwesomeIcon icon={faUser} className="form-icon" />
                  Nom <span className="required">*</span>
                </Form.Label>
                <div className={`input-glass ${formErrors.nom ? 'error' : ''}`}>
                  <Form.Control
                    type="text"
                    name="nom"
                    value={form.nom}
                    onChange={handleInputChange}
                    placeholder="Nom de famille"
                    isInvalid={!!formErrors.nom}
                    disabled={submitting}
                  />
                </div>
                {formErrors.nom && <div className="error-text">{formErrors.nom}</div>}
              </div>

              <div className="form-group">
                <Form.Label>
                  <FontAwesomeIcon icon={faUser} className="form-icon" />
                  Prénom <span className="required">*</span>
                </Form.Label>
                <div className={`input-glass ${formErrors.prenom ? 'error' : ''}`}>
                  <Form.Control
                    type="text"
                    name="prenom"
                    value={form.prenom}
                    onChange={handleInputChange}
                    placeholder="Prénom"
                    isInvalid={!!formErrors.prenom}
                    disabled={submitting}
                  />
                </div>
                {formErrors.prenom && <div className="error-text">{formErrors.prenom}</div>}
              </div>

              <div className="form-group">
                <Form.Label>
                  <FontAwesomeIcon icon={faEnvelope} className="form-icon" />
                  Email <span className="required">*</span>
                </Form.Label>
                <div className={`input-glass ${formErrors.email ? 'error' : ''}`}>
                  <Form.Control
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleInputChange}
                    placeholder="exemple@domaine.com"
                    isInvalid={!!formErrors.email}
                    disabled={submitting}
                  />
                </div>
                {formErrors.email && <div className="error-text">{formErrors.email}</div>}
              </div>

              <div className="form-group">
                <Form.Label>
                  <FontAwesomeIcon icon={faPhone} className="form-icon" />
                  Téléphone
                </Form.Label>
                <div className="input-glass">
                  <Form.Control
                    type="text"
                    name="tel"
                    value={form.tel}
                    onChange={handleInputChange}
                    placeholder="Numéro de téléphone"
                    disabled={submitting}
                  />
                </div>
              </div>

              <div className="form-group">
                <Form.Label>
                  <FontAwesomeIcon icon={faCalendarAlt} className="form-icon" />
                  Date de naissance
                </Form.Label>
                <div className="input-glass">
                  <Form.Control
                    type="date"
                    name="date_naisse"
                    value={form.date_naisse}
                    onChange={handleInputChange}
                    disabled={submitting}
                  />
                </div>
              </div>

              <div className="form-group">
                <Form.Label>
                  <FontAwesomeIcon icon={faVenusMars} className="form-icon" />
                  Sexe
                </Form.Label>
                <div className="input-glass">
                  <Form.Select name="sexe" value={form.sexe} onChange={handleInputChange} disabled={submitting}>
                    <option value="">Non défini</option>
                    <option value="Homme">Homme</option>
                    <option value="Femme">Femme</option>
                  </Form.Select>
                </div>
              </div>

              <div className="form-group">
                <Form.Label>
                  <FontAwesomeIcon icon={faIdCard} className="form-icon" />
                  Fonction <span className="required">*</span>
                </Form.Label>
                <div className={`input-glass ${formErrors.fonction ? 'error' : ''}`}>
                  <Form.Select
                    name="fonction"
                    value={form.fonction}
                    onChange={handleInputChange}
                    isInvalid={!!formErrors.fonction}
                    disabled={submitting}
                  >
                    <option className="bg-dark" value="">-- Sélectionnez une fonction --</option>
                    <option className="bg-dark" value="SUPER ADMIN">SUPER ADMIN</option>
                    <option className="bg-dark" value="DSI">DSI</option>
                    <option className="bg-dark" value="RH">RH</option>
                    <option className="bg-dark" value="SUPERVISOR">SUPERVISOR</option>
                    <option className="bg-dark" value="FORMATEUR">FORMATEUR</option>
                    <option className="bg-dark" value="COMPTABLE">COMPTABLE</option>
                    <option className="bg-dark" value="CAISSIER">CAISSIER</option>
                  </Form.Select>
                </div>
                {formErrors.fonction && <div className="error-text">{formErrors.fonction}</div>}
              </div>

              <div className="form-group">
                <Form.Label>
                  <FontAwesomeIcon icon={faMapMarkerAlt} className="form-icon" />
                  Adresse <span className="required">*</span>
                </Form.Label>
                <div className={`input-glass ${formErrors.adresse ? 'error' : ''}`}>
                  <Form.Control
                    type="text"
                    name="adresse"
                    value={form.adresse}
                    onChange={handleInputChange}
                    placeholder="Adresse complète"
                    isInvalid={!!formErrors.adresse}
                    disabled={submitting}
                  />
                </div>
                {formErrors.adresse && <div className="error-text">{formErrors.adresse}</div>}
              </div>

              <div className="form-group">
                <Form.Label>
                  <FontAwesomeIcon icon={faCircle} className="form-icon" />
                  Statut
                </Form.Label>
                <div className="input-glass">
                  <Form.Select name="status" value={form.status} onChange={handleInputChange} disabled={submitting}>
                    <option className="bg-dark" value="0">Inactif</option>
                    <option className="bg-dark" value="1">Actif</option>
                  </Form.Select>
                </div>
              </div>

              <div className="form-group">
                <Form.Label>
                  <FontAwesomeIcon icon={faKey} className="form-icon" />
                  Mot de passe {!editingId && <span className="required">*</span>}
                </Form.Label>
                <div className={`input-glass ${formErrors.pw ? 'error' : ''}`}>
                  <Form.Control
                    type={showPassword ? 'text' : 'password'}
                    name="pw"
                    value={form.pw}
                    onChange={handleInputChange}
                    placeholder={editingId ? 'Laisser vide pour ne pas modifier' : 'Nouveau mot de passe'}
                    isInvalid={!!formErrors.pw}
                    disabled={submitting}
                  />
                  <span
                    className="input-append"
                    onClick={() => setShowPassword(!showPassword)}
                    title={showPassword ? 'Masquer' : 'Afficher'}
                  >
                    <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                  </span>
                </div>
                {formErrors.pw && <div className="error-text">{formErrors.pw}</div>}
              </div>
            </div>

            <Modal.Footer>
              <button type="button" className="btn-modal-cancel" onClick={() => setShowModal(false)} disabled={submitting}>
                <FontAwesomeIcon icon={faTimes} className="me-2" /> Annuler
              </button>
              <button type="submit" className="btn-modal-submit" disabled={submitting}>
                <FontAwesomeIcon icon={editingId ? faCheckCircle : faPlus} className="me-2" />
                {submitting ? 'En cours...' : (editingId ? 'Mettre à jour' : 'Ajouter')}
              </button>
            </Modal.Footer>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Toast */}
      {toast && (
        <div className="toast-container">
          <div className={`toast-item ${toast.variant}`}>
            <FontAwesomeIcon
              icon={toast.variant === 'success' ? faCheckCircle : faExclamationTriangle}
              className={toast.variant === 'success' ? 'text-success' : 'text-danger'}
            />
            <span>{toast.msg}</span>
            <button className="btn-close-custom" onClick={() => setToast(null)}>✕</button>
          </div>
        </div>
      )}
    </>
  );
};

export default ManageAdmins;